import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import useDeleteStripeTerminalReader from 'hooks/stripe/useDeleteStripeTerminalReader';
import { StripeTerminalReader } from 'types/generated';
import notificationHandler from 'utils/notifications';

import StripeTerminalReaderListItem from './StripeTerminalReaderListItem';

interface StripeTerminalReaderDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  stripeTerminalReader: StripeTerminalReader;
}

const StripeTerminalReaderDeleteModal = ({
  opened,
  onClose,
  stripeTerminalReader,
}: StripeTerminalReaderDeleteModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const deleteMutation = useDeleteStripeTerminalReader();

  const handleDelete = (): void => {
    deleteMutation.mutate(stripeTerminalReader.stripeTerminalReaderId, {
      onSuccess: () => {
        successNotification('Reader deleted', 'Your card reader has been removed.');
        onClose();
      },
      onError: error => {
        errorNotification('Deletion failed', error.response?.data?.error ?? 'An error occurred.');
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Title order={3} align='center'>
          Are you sure you want to delete this card reader?
        </Title>
      }
      centered
      size='lg'
      padding='xl'
    >
      <Stack spacing='md'>
        <Text>By deleting this card reader you will no longer be able to receive in person payments.</Text>

        <StripeTerminalReaderListItem stripeTerminalReader={stripeTerminalReader} />

        <Group mt='xl' px='xl' sx={{ justifyContent: 'space-evenly' }}>
          <Button onClick={onClose} disabled={deleteMutation.isLoading}>
            Go Back
          </Button>
          <Button variant='outline' onClick={handleDelete} loading={deleteMutation.isLoading}>
            Delete Reader
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StripeTerminalReaderDeleteModal;
